import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Patient } from '@models/patient';
import {
  ConnectionState,
  Conversation,
  Client as ConversationsClient,
  Message,
  SendMediaOptions,
  User,
} from '@twilio/conversations';
import { BehaviorSubject, Subject } from 'rxjs';
import { ClinicsService } from './clinics.service';
import { PatientService } from './patient.service';
import { UsersService } from './users.service';

export enum ConversationState {
  Active = 'active',
  Inactive = 'inactive',
  Closed = 'closed',
}

@Injectable({
  providedIn: 'root',
})
export class TwilioConversationsService {
  initialized = false;
  clientUser: User;
  private clinicId: number;
  private client: ConversationsClient;

  private conversations = new BehaviorSubject<Conversation[]>(null);
  conversations$ = this.conversations.asObservable();

  private selectedConversationAndPatient = new BehaviorSubject<[Conversation, Patient]>([null, null]);
  selectedConversationAndPatient$ = this.selectedConversationAndPatient.asObservable();

  private conversationUpdated = new Subject<Conversation>();
  conversationUpdated$ = this.conversationUpdated.asObservable();

  private messageAdded = new Subject<Message>();
  messageAdded$ = this.messageAdded.asObservable();

  private totalUnreadCount = new BehaviorSubject<number>(0);
  totalUnreadCount$ = this.totalUnreadCount.asObservable();

  private connectionState = new BehaviorSubject<ConnectionState | 'token'>(null);
  connectionState$ = this.connectionState.asObservable();

  constructor(
    private http: HttpClient,
    private clinicsService: ClinicsService,
    private usersService: UsersService,
    private patientService: PatientService
  ) {
    this.clinicsService.clinicIdSelected$.subscribe(async (clinicId) => {
      if (clinicId && this.clinicId != clinicId) {
        this.clinicId = clinicId;
        if (this.clinicsService.isTwilioEnabled) {
          await this.initConversationsClient();
        } else {
          this.initialized = false;
          this.conversations.next([]);
        }
      }
    });
  }

  sendOptInMessage(patientId: number) {
    return this.http.post<void>(environment.baseUrl + 'api/Twilio/SendOptInSms/' + patientId, {});
  }

  archiveConversation(conversationSid: string) {
    return this.http.put<void>(environment.baseUrl + 'api/Twilio/ArchiveConversation/' + conversationSid, {});
  }

  private getPatientConversationSid(patientId: number) {
    return this.http.get(
      environment.baseUrl + 'api/Twilio/GetPatientConversation/' + patientId,
      { responseType: 'text' }
    );
  }

  private createPatientConversationSid(patientId: number) {
    return this.http.post(
      environment.baseUrl + 'api/Twilio/CreatePatientConversation/' + patientId,
      {},
      { responseType: 'text' }
    );
  }

  private getAccessToken() {
    return this.http.get(environment.baseUrl + 'api/Twilio', { responseType: 'text' });
  }

  private async initConversationsClient() {
    console.log('Patient Messaging - Initializing...');
    this.initialized = false;
    try {
      const token = await this.getAccessToken().toPromise();
      if (!token) throw new Error("A Twilio access token could not be generated with this clinic's credentials.");
      this.client = new ConversationsClient(token);
      this.clientUser = this.client.user;
    } catch (error) {
      console.error(error);
      this.connectionState.next('token');
      this.conversations.next([]);
      return;
    }
    this.listenToEvents();
  }

  private listenToEvents() {
    if (!this.client) return;
    this.client.removeAllListeners();

    this.client.on('connectionStateChanged', (state) => {
      console.log(`Patient Messaging State: ${state}`);
      this.connectionState.next(state);
    });

    this.client.on('initFailed', (error: any) => {
      console.log('Patient Messaging - Initializing Failed.');
    });

    this.client.on('connectionError', (error: any) => {
      console.log('Patient Messaging - Connection Error.');
    });

    this.client.on('tokenAboutToExpire', async () => {
      const token = await this.getAccessToken().toPromise();
      this.client = await this.client.updateToken(token);
    });

    this.client.on('tokenExpired', () => {
      console.log('Patient Messaging - Token Expired, Reinitializing.');
      this.client.removeAllListeners();
      this.initConversationsClient();
    });

    this.client.on('initialized', async () => {
      await this.getClinicConversations();
      this.initialized = true;
      console.log('Patient Messaging - Initialized.');
    });

    this.client.on('conversationLeft', (conversation) => {
      if (this.conversations.value) {
        this.conversations.next(this.conversations.value.filter((con) => con.sid != conversation.sid));
      }
    });

    this.client.on('conversationJoined', async (conversation) => {
      if (this.initialized) {
        await conversation.setAllMessagesUnread();
        const existing = this.conversations.value ?? [];
        if (!existing.some((con) => con.sid == conversation.sid)) {
          const conversations = this.sortConversations([conversation, ...existing]);
          this.conversations.next(conversations);
        }
      }
    });

    this.client.on('conversationUpdated', async (conversationUpdate) => {
      const updateConversation = conversationUpdate.conversation;
      const conversations = this.conversations.value ?? [];
      const [selectedConversation, _] = this.selectedConversationAndPatient.value;

      if (selectedConversation && selectedConversation.sid === updateConversation.sid) {
        await selectedConversation.setAllMessagesRead().catch((error) => {
          throw error;
        });
      }

      const conIndex = conversations.findIndex((con) => con.sid == updateConversation.sid);
      if (conIndex > -1) {
        conversations[conIndex] = conversationUpdate.conversation;
        this.conversations.next([...conversations]);
        await this.setTotalUnreadCount();
      }

      this.conversationUpdated.next(updateConversation);
    });

    this.client.on('messageAdded', async (message: Message) => {
      const messageConversation = message.conversation;
      const allConversations = this.conversations.value ?? [];
      const activeConversations = allConversations.filter((con) => con.state.current === ConversationState.Active);
      const [selectedConversation, _] = this.selectedConversationAndPatient.value;

      if (selectedConversation && selectedConversation.sid === messageConversation.sid) {
        await selectedConversation.setAllMessagesRead().catch((error) => {
          throw error;
        });
      }

      const inLoadedConversations = activeConversations.some((con) => con.sid == messageConversation.sid);
      if (inLoadedConversations) {
        const conversations = this.sortConversations(allConversations);
        this.conversations.next(conversations);
        await this.setTotalUnreadCount();
      }

      this.messageAdded.next(message);
    });
  }

  setSelectedConversationAndPatient(conversation: Conversation, patient: Patient) {
    this.selectedConversationAndPatient.next([conversation, patient]);
  }
  /**
   * Gets the conversation for a patient, only active conversations are returned by default
   * @param patientId The id of the patient
   * @param activeOnly Whether to only return active conversations
   * @returns The conversation for the patient
   */
  async getPatientConversation(patientId: number): Promise<Conversation> {
    if (!this.client) throw new Error('SMS client not initialized. Credentials may be invalid.');
    const existingConversationSid = await this.getPatientConversationSid(patientId).toPromise();
    if (!existingConversationSid) return null;
    const existingConversation = await this.client.getConversationBySid(existingConversationSid);
    return existingConversation;
  }

  /**
   * Creates a conversation for a patient, if one does not exist. If one does exist, it will be returned.
   * If the conversation is archived (inactive or closed), it will be unarchived and returned.
   * @param patientId The id of the patient
   * @returns The conversation for the patient
   */
  async createPatientConversation(patientId: number): Promise<Conversation> {
    if (!this.client) throw new Error('SMS client not initialized. Credentials may be invalid.');
    let conversationSid =
      (await this.getPatientConversationSid(patientId).toPromise()) ??
      (await this.createPatientConversationSid(patientId).toPromise());
    return await this.client.getConversationBySid(conversationSid);
  }

  async sendMessage(conversation: Conversation, body: string, image?: File): Promise<void> {
    if (conversation) {
      const user = this.usersService.loggedInUser;
      const attributes = {
        user: {
          id: user.id,
          fullName: user.fullName,
          firstName: user.firstName,
          lastName: user.lastName,
          avatar: user.avatar.split('?')[0],
        },
      };

      const messageBuilder = conversation.prepareMessage().setBody(body).setAttributes(attributes);
      if (image) {
        const sendMediaOptions: SendMediaOptions = {
          contentType: image.type,
          filename: image.name,
          media: image,
        };
        messageBuilder.addMedia(sendMediaOptions);
      }

      const message = messageBuilder.build();
      try {
        await message.send();
      } catch (error) {
        if (error.body?.message) {
          throw new Error(error.body?.message);
        }
        throw error;
      }
    }
  }

  async setMessageReadBy(message: Message): Promise<void> {
    if (message) {
      const participant = await message.getParticipant().catch(() => {
        return null;
      });
      const attributes = message.attributes as any;
      if (participant && participant.type !== 'chat' && !attributes.readByUser) {
        const user = this.usersService.loggedInUser;
        attributes.readByUser = {
          id: user.id,
          fullName: user.fullName,
          firstName: user.firstName,
          lastName: user.lastName,
          avatar: user.avatar.split('?')[0],
        };
        attributes.readByDate = new Date();
        message = await message.updateAttributes(attributes).catch((error) => {
          throw error;
        });
      }
    }
  }

  parsePatientId(conversation: Conversation): number {
    const patientPart = conversation.uniqueName?.split('-')[1];
    const patientId = patientPart?.split('_')[1];
    return Number(patientId);
  }

  async setTotalUnreadCount() {
    let total = 0;
    if (this.conversations.value) {
      const activeConversations = this.conversations.value.filter(
        (con) => con.state.current === ConversationState.Active
      );
      for (const conversation of activeConversations) {
        const count = await conversation.getUnreadMessagesCount();
        total += count ?? 0;
      }
    }
    this.totalUnreadCount.next(total);
  }

  private async getClinicConversations() {
    if (!this.client) throw new Error('Twilio client not initialized. Credentials may be invalid.');
    let conversationsPage = await this.client.getSubscribedConversations().catch((error) => {
      throw error;
    });
    let conversations = conversationsPage.items;
    while (conversationsPage.hasNextPage) {
      conversationsPage = await conversationsPage.nextPage();
      conversations.push(...conversationsPage.items);
    }
    conversations = this.sortConversations(conversations);
    this.conversations.next(conversations);
    await this.setTotalUnreadCount();
  }

  private sortConversations(conversations: Conversation[]): Conversation[] {
    return conversations.sort((a, b) => {
      let aComp = a.lastMessage ? a.lastMessage.dateCreated.getTime() : a.dateCreated.getTime();
      let bComp = b.lastMessage ? b.lastMessage.dateCreated.getTime() : b.dateCreated.getTime();
      return bComp - aComp;
    });
  }
}
